import * as XLSX from 'xlsx-js-style';
import { generateId, parseDateToISO } from '../utils/helpers';
import { detectTxType, extractReferences } from './pdfParser';

export const EXCEL_TEMPLATE_COLUMNS = ['DATE', 'DOC NO', 'NARRATION', 'DEBIT', 'CREDIT'];

// ═══ PDF → Excel Converter Export ═══
export const exportPdfToExcel = (transactions, metadata, filename) => {
  const wb = XLSX.utils.book_new();

  // ── Styles ──
  const hdrStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { fgColor: { rgb: "333333" } },
    border: { top: {style:'thin'}, bottom: {style:'medium'}, left: {style:'thin'}, right: {style:'thin'} },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const cellBorder = { border: { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} }, alignment: { vertical: 'top' } };
  const numStyle = { ...cellBorder, numFmt: '#,##,##0.00', alignment: { horizontal: 'right', vertical: 'top' } };
  const dateStyle = { ...cellBorder, alignment: { horizontal: 'center', vertical: 'top' } };
  const titleStyle = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center' } };
  const subTitleStyle = { font: { bold: false, sz: 11, color: { rgb: "555555" } }, alignment: { horizontal: 'center' } };
  const balStyle = { font: { bold: true, sz: 11 }, numFmt: '#,##,##0.00' };
  const totalLabelStyle = { font: { bold: true, sz: 11 }, border: { top: {style:'medium'}, bottom: {style:'double'} }, alignment: { horizontal: 'right' } };
  const totalNumStyle = { font: { bold: true, sz: 11 }, numFmt: '#,##,##0.00', border: { top: {style:'medium'}, bottom: {style:'double'} }, alignment: { horizontal: 'right' } };

  // ── Build rows: Tally format — Date | Particulars | Vch Type | Vch No. | Debit | Credit ──
  const rows = [];
  let rowIdx = 0;

  // Title rows (company/supplier info)
  const companyName = metadata?.company || '';
  const supplierName = metadata?.supplier || '';
  const period = metadata?.period || '';
  if (companyName || supplierName) {
    rows.push([companyName || supplierName]); rowIdx++;
    if (supplierName && companyName) { rows.push([supplierName]); rowIdx++; }
    if (period) { rows.push([period]); rowIdx++; }
    rows.push([]); rowIdx++; // blank line
  }

  // Header row
  const headerRowIdx = rowIdx;
  const headers = ['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit'];
  rows.push(headers); rowIdx++;

  // Opening Balance row
  if (metadata?.openingBalance != null) {
    const ob = metadata.openingBalance;
    const obAbs = Math.abs(ob);
    rows.push([
      '', 'Opening Balance', '', '',
      ob >= 0 ? obAbs : '',  // DR → Debit column
      ob < 0 ? obAbs : '',   // CR → Credit column
    ]);
    rowIdx++;
  }

  // Transaction rows
  const dataStartRow = rowIdx;
  transactions.forEach(t => {
    rows.push([
      t.date || '',
      (t.narration || '').substring(0, 300),
      t.type || '',
      t.docNo || '',
      t.debit || '',
      t.credit || '',
    ]);
    rowIdx++;
  });

  // Totals row
  const totalDr = transactions.reduce((s, t) => s + (t.debit || 0), 0);
  const totalCr = transactions.reduce((s, t) => s + (t.credit || 0), 0);
  const totalRowIdx = rowIdx;
  rows.push(['', '', '', 'TOTAL', totalDr, totalCr]);
  rowIdx++;

  // Closing Balance row
  if (metadata?.closingBalance != null) {
    const cb = metadata.closingBalance;
    const cbAbs = Math.abs(cb);
    rows.push([
      '', 'Closing Balance', '', '',
      cb >= 0 ? cbAbs : '',
      cb < 0 ? cbAbs : '',
    ]);
    rowIdx++;
  }

  // ── Create worksheet ──
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // ── Apply styles ──
  // Title rows
  for (let r = 0; r < headerRowIdx; r++) {
    const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })];
    if (cell) cell.s = r === 0 ? titleStyle : subTitleStyle;
  }
  // Merge title rows across all columns
  if (!ws['!merges']) ws['!merges'] = [];
  for (let r = 0; r < headerRowIdx; r++) {
    if (rows[r] && rows[r][0]) {
      ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: 5 } });
    }
  }

  // Header row styling
  for (let c = 0; c < headers.length; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: headerRowIdx, c })];
    if (cell) cell.s = hdrStyle;
  }

  // Data rows styling
  for (let r = headerRowIdx + 1; r < totalRowIdx; r++) {
    for (let c = 0; c < 6; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) continue;
      if (c === 0) cell.s = dateStyle;           // Date
      else if (c === 4 || c === 5) cell.s = numStyle; // Debit/Credit
      else cell.s = cellBorder;                   // Particulars, Vch Type, Vch No
    }
  }

  // Total row styling
  for (let c = 0; c < 6; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: totalRowIdx, c })];
    if (cell) cell.s = (c === 4 || c === 5) ? totalNumStyle : totalLabelStyle;
  }

  // Closing balance styling
  if (metadata?.closingBalance != null) {
    const cbRow = totalRowIdx + 1;
    for (let c = 0; c < 6; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r: cbRow, c })];
      if (cell) cell.s = (c === 4 || c === 5) ? { ...balStyle, alignment: { horizontal: 'right' } } : { font: { bold: true } };
    }
  }

  // Column widths — match Tally PDF layout
  ws['!cols'] = [
    { wch: 12 },  // Date
    { wch: 55 },  // Particulars
    { wch: 18 },  // Vch Type
    { wch: 16 },  // Vch No.
    { wch: 16 },  // Debit
    { wch: 16 },  // Credit
  ];

  // Row height for header
  if (!ws['!rows']) ws['!rows'] = [];
  ws['!rows'][headerRowIdx] = { hpt: 22 };

  XLSX.utils.book_append_sheet(wb, ws, 'Ledger');

  // ── Download ──
  const fn = filename || 'PDF_Converted.xlsx';
  XLSX.writeFile(wb, fn.endsWith('.xlsx') ? fn : fn + '.xlsx');
};

export const exportReconciliationExcel = (session, allCategories, catMap) => {
  const wb = XLSX.utils.book_new();
  
  const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "0D9488" } }, border: { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} } };
  const titleStyle = { font: { bold: true, sz: 14 } };
  const cellStyle = { border: { top: {style:'thin'}, bottom: {style:'thin'}, left: {style:'thin'}, right: {style:'thin'} } };
  const numStyle = { ...cellStyle, numFmt: '#,##0.00' };
  
  // 1. SUMMARY SHEET
  const summaryData = [];
  summaryData.push([{ v: session.company || 'COMPANY', s: titleStyle }, null, null, null]);
  summaryData.push([{ v: `RECONCILIATION STATEMENT AS ON ${session.recoDate ? parseDateToISO(session.recoDate) : ''}`, s: titleStyle }, null, null, null]);
  summaryData.push([null, null, null, null]);
  
  const suppNetMovement = session.supplierLedger.reduce((s,t) => s + (t.debit||0) - (t.credit||0), 0);
  const compNetMovement = session.companyLedger.reduce((s,t) => s + (t.debit||0) - (t.credit||0), 0);
  const suppClosingBal = session.closingBalanceSupplier != null ? session.closingBalanceSupplier : ((session.openingBalanceSupplier || 0) + suppNetMovement);
  const compClosingBal = session.closingBalanceCompany != null ? session.closingBalanceCompany : ((session.openingBalanceCompany || 0) + compNetMovement);

  let rIdx = 3;
  // Use absolute payable amounts for reconciliation
  const suppPayable = Math.abs(suppClosingBal);
  const compPayable = Math.abs(compClosingBal);
  summaryData.push([{v:`BALANCE AS PER SUPPLIER LEDGER (CLOSING) ${suppClosingBal <= 0 ? 'CR' : 'DR'}`, s:{font:{bold:true}}}, null, null, {v:suppPayable, t:'n', s:numStyle}]);
  const suppBalRow = rIdx + 1;
  rIdx++;
  
  let lessItems = [];
  let addItems = [];
  
  if (session.recoItems && session.recoItems.length > 0) {
    lessItems = session.recoItems.filter(i => i.type === 'LESS');
    addItems = session.recoItems.filter(i => i.type === 'ADD');
  } else {
    const uComp = session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
    const uSupp = session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
    const buckets = {};
    const addToBucket = (cid, isComp, t) => {
      if (!buckets[cid]) buckets[cid] = { cNet: 0, sNet: 0 };
      if (isComp) buckets[cid].cNet += (t.debit || 0) - (t.credit || 0);
      else buckets[cid].sNet += (t.debit || 0) - (t.credit || 0);
    };

    uComp.forEach(t => addToBucket(t.categoryId || 'other', true, t));
    uSupp.forEach(t => addToBucket(catMap[t.categoryId || 'other'] || t.categoryId || 'other', false, t));

    Object.keys(buckets).forEach(cid => {
      const b = buckets[cid];
      const cat = allCategories.find(c => c.id === cid) || { label: cid };
      if (Math.abs(b.cNet) > 0.01) {
        if (b.cNet > 0) lessItems.push({ label: `${cat.label.toUpperCase()} - IN COMPANY ONLY (DR)`, amount: Math.abs(b.cNet) });
        else addItems.push({ label: `${cat.label.toUpperCase()} - IN COMPANY ONLY (CR)`, amount: Math.abs(b.cNet) });
      }
      if (Math.abs(b.sNet) > 0.01) {
        // Supplier DR (sale/invoice) = supplier higher → LESS to reach company
        // Supplier CR (receipt) = supplier lower → ADD to reach company
        if (b.sNet > 0) lessItems.push({ label: `${cat.label.toUpperCase()} - IN SUPPLIER ONLY (DR)`, amount: Math.abs(b.sNet) });
        else addItems.push({ label: `${cat.label.toUpperCase()} - IN SUPPLIER ONLY (CR)`, amount: Math.abs(b.sNet) });
      }
    });
  }

  const lessRows = [];
  lessItems.forEach((item, idx) => {
     summaryData.push([{v: idx === 0 ? 'LESS:-' : '', s:{font:{bold:true}}}, {v:item.label}, null, {v:item.amount, t:'n', s:numStyle}]);
     rIdx++;
     lessRows.push(`D${rIdx}`);
  });
  
  const addRows = [];
  addItems.forEach((item, idx) => {
     summaryData.push([{v: idx === 0 ? 'ADD:-' : '', s:{font:{bold:true}}}, {v:item.label}, null, {v:item.amount, t:'n', s:numStyle}]);
     rIdx++;
     addRows.push(`D${rIdx}`);
  });
  
  summaryData.push([null, null, null, null]);
  rIdx++;
  
  // Create calculated formula: = D{suppBalRow} + SUM(addRows) - SUM(lessRows)
  const addFormula = addRows.length > 0 ? `+SUM(${addRows.join(',')})` : '';
  const lessFormula = lessRows.length > 0 ? `-SUM(${lessRows.join(',')})` : '';
  const calcFormula = `D${suppBalRow}${addFormula}${lessFormula}`;
  
  summaryData.push([{v:'BALANCE AS PER OUR LEDGER (Calculated)', s:{font:{bold:true}}}, null, null, {f:calcFormula, t:'n', s:{...numStyle, font:{bold:true}}}]);
  rIdx++;
  summaryData.push([{v:`ACTUAL COMPANY LEDGER BALANCE (CLOSING) ${compClosingBal <= 0 ? 'CR' : 'DR'}`, s:{font:{bold:true}}}, null, null, {v:compPayable, t:'n', s:{...numStyle, font:{bold:true}}}]);
  
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [{wch: 15}, {wch: 60}, {wch: 15}, {wch: 20}];
  XLSX.utils.book_append_sheet(wb, wsSummary, "SUMMARY");
  
  // ── Helper to group by doc number ──
  const groupAndNet = (items) => {
    const groups = {};
    const out = [];
    items.forEach(t => {
      const doc = (t.docNo||'').trim().toUpperCase();
      if(!doc || doc.length < 3) { out.push({...t}); return; }
      if(!groups[doc]) groups[doc] = { ...t, debit: 0, credit: 0, _count: 0, earliestDate: t.date };
      groups[doc]._count++;
      groups[doc].debit += (t.debit||0);
      groups[doc].credit += (t.credit||0);
      if(t.date < groups[doc].earliestDate) groups[doc].earliestDate = t.date;
    });
    return Object.values(groups).map(g => {
      if(g._count === 1) return g;
      const net = g.debit - g.credit;
      g.date = g.earliestDate;
      if (net >= 0) { g.debit = net; g.credit = 0; }
      else { g.debit = 0; g.credit = Math.abs(net); }
      return g;
    });
  };

  // 2. DETAILS SHEETS
  const unmatchedComp = session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
  const unmatchedSupp = session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
  const targetCategory = (t, isComp) => isComp ? (t.categoryId || 'other') : (catMap[t.categoryId || 'other'] || t.categoryId || 'other');
  const cats = new Set([...unmatchedComp.map(t=>targetCategory(t,true)), ...unmatchedSupp.map(t=>targetCategory(t,false))]);
  
  cats.forEach(cid => {
    if(!cid) return;
    const catName = allCategories.find(c => c.id === cid)?.label || cid;
    
    let cItems = unmatchedComp.filter(t => targetCategory(t, true) === cid);
    let sItems = unmatchedSupp.filter(t => targetCategory(t, false) === cid);
    
    if (cItems.length === 0 && sItems.length === 0) return;
    
    cItems = groupAndNet(cItems);
    sItems = groupAndNet(sItems);
    
    const rows = [];
    rows.push([{v: catName.toUpperCase(), s: titleStyle}, null, null, null, null, null]);
    rows.push([
      {v:'SIDE',s:headerStyle}, {v:'DATE',s:headerStyle}, {v:'DOC NO',s:headerStyle}, 
      {v:'NARRATION',s:headerStyle}, {v:'DEBIT',s:headerStyle}, {v:'CREDIT',s:headerStyle}
    ]);
    
    let drStartRow = 3;
    let drEndRow = 2;
    
    cItems.forEach(t => {
      rows.push([{v:'COMP', s:cellStyle}, {v:t.date, s:cellStyle}, {v:t.docNo, s:cellStyle}, {v:t.narration, s:cellStyle}, {v:t.debit||0, t:'n', s:numStyle}, {v:t.credit||0, t:'n', s:numStyle}]);
      drEndRow++;
    });
    sItems.forEach(t => {
      rows.push([{v:'SUPP', s:cellStyle}, {v:t.date, s:cellStyle}, {v:t.docNo, s:cellStyle}, {v:t.narration, s:cellStyle}, {v:t.debit||0, t:'n', s:numStyle}, {v:t.credit||0, t:'n', s:numStyle}]);
      drEndRow++;
    });
    
    rows.push([
      null, null, null, {v:'TOTAL', s:{...cellStyle, font:{bold:true}}}, 
      {f:`SUM(E${drStartRow}:E${drEndRow})`, t:'n', s:{...numStyle, font:{bold:true}}}, 
      {f:`SUM(F${drStartRow}:F${drEndRow})`, t:'n', s:{...numStyle, font:{bold:true}}}
    ]);
    
    let safeName = catName.substring(0, 31).replace(/[\\/?*[\]]/g, ' ').trim();
    if(!safeName) safeName = 'Tab';
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{wch: 8}, {wch: 12}, {wch: 15}, {wch: 45}, {wch: 15}, {wch: 15}];
    try { XLSX.utils.book_append_sheet(wb, ws, safeName); } catch(e) {}
  });

  XLSX.writeFile(wb, `Recon_Summary_${session.company || 'Session'}.xlsx`);
};

export const downloadExcelTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([EXCEL_TEMPLATE_COLUMNS]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Template");
  XLSX.writeFile(wb, "Ledger_Upload_Template.xlsx");
};

export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (rawRows.length < 2) {
          throw new Error("Excel file is empty or missing data.");
        }

        // Find header row (might not be first row, but let's assume it's one of the first few)
        let headerRowIndex = -1;
        let colMappings = {};
        
        for (let i = 0; i < Math.min(10, rawRows.length); i++) {
          const row = rawRows[i].map(c => (c || '').toString().toUpperCase().trim());
          if (row.includes('DATE') && (row.includes('CREDIT') || row.includes('CR'))) {
            headerRowIndex = i;
            const findCol = (...names) => {
              for (const name of names) {
                const idx = row.indexOf(name);
                if (idx >= 0) return idx;
              }
              return -1;
            };
            colMappings = {
              date: row.indexOf('DATE'),
              docNo: findCol('DOC NO', 'DOC NO.', 'VCH NO.', 'VCH NO', 'REFERENCE', 'DOC'),
              narration: findCol('NARRATION', 'PARTICULARS', 'DESCRIPTION'),
              debit: findCol('DEBIT', 'DR', 'DEBIT AMT', 'DEBIT AMOUNT'),
              credit: findCol('CREDIT', 'CR', 'CREDIT AMT', 'CREDIT AMOUNT'),
            };
            break;
          }
        }

        if (headerRowIndex === -1) {
          throw new Error("Could not find required columns (DATE, CREDIT/DEBIT) in Excel file. Please use the template.");
        }

        const transactions = [];
        let validLineCount = 0;
        let openingBalance = null;
        let closingBalance = null;

        const parseAmt = (val) => {
          if (!val) return 0;
          if (typeof val === 'number') return val;
          const str = val.toString().replace(/[^\d.-]/g, '');
          return parseFloat(str) || 0;
        };

        for (let i = headerRowIndex + 1; i < rawRows.length; i++) {
          const row = rawRows[i];
          if (!row || row.length === 0) continue;

          let dateRaw = colMappings.date >= 0 ? row[colMappings.date] : '';
          let docNo = colMappings.docNo >= 0 ? row[colMappings.docNo] || '' : '';
          let narration = colMappings.narration >= 0 ? row[colMappings.narration] || '' : '';
          let debitRaw = colMappings.debit >= 0 ? row[colMappings.debit] : 0;
          let creditRaw = colMappings.credit >= 0 ? row[colMappings.credit] : 0;

          // Excel date conversion if it's a serial number
          if (typeof dateRaw === 'number') {
            // Account for Lotus 1-2-3 leap year bug: Excel treats 1900 as a leap year
            // Serial numbers >= 60 are off by 1 day due to the phantom Feb 29, 1900
            let serial = dateRaw;
            if (serial >= 60) serial -= 1;
            const excelEpoch = new Date(Date.UTC(1899, 11, 31));
            const dateObj = new Date(excelEpoch.getTime() + serial * 86400000);
            dateRaw = dateObj.toISOString().split('T')[0];
          }

          const dateStr = dateRaw ? dateRaw.toString().trim() : '';
          const parsedDate = parseDateToISO(dateStr) || dateStr;

          const debit = parseAmt(debitRaw);
          const credit = parseAmt(creditRaw);

          // Extract Opening/Closing Balance before skipping
          const norm = `${docNo || ''} ${narration || ''}`.toLowerCase().replace(/[\s_]+/g, '');
          if (norm.includes('openingbalance') || norm.includes('broughtforward') || norm.includes('broughtdown')) {
            if (openingBalance === null && (debit > 0 || credit > 0)) {
              // Credit opening = negative (payable), Debit opening = positive (receivable)
              openingBalance = credit > 0 ? -credit : debit;
            }
            continue;
          }
          if (norm.includes('closingbalance') || norm.includes('carriedforward') || norm.includes('carrieddown') || norm.includes('grandtotal') || norm.endsWith('total') || norm === 'total' || norm === 'crtotal') {
            if (closingBalance === null && (norm.includes('closingbalance') || norm.includes('carriedforward') || norm.includes('carrieddown'))) {
              if (debit > 0 || credit > 0) {
                closingBalance = credit > 0 ? -credit : debit;
              }
            }
            continue;
          }

          if (!parsedDate) continue; // Must have date
          if (debit === 0 && credit === 0) continue;
          const strippedNarr = String(narration || '').trim();
          if (strippedNarr.length > 0 && /^[\d,.\s\-+()₹$€£¥]+$/.test(strippedNarr)) {
            continue;
          }

          validLineCount++;
          const docStr = docNo.toString().trim();
          const narrStr = narration.toString().trim();
          const type = detectTxType(docStr, narrStr);
          const refs = extractReferences(`${docStr} ${narrStr}`);

          // Auto-categorization: map transaction type → categoryId
          const typeToCat = {
            'Purchase': 'purchase', 'Sales': 'sales', 'Payment': 'payment',
            'Receipt': 'receipt', 'Debit Note': 'debit_note', 'Credit Note': 'credit_note',
            'Excess/Shortage': 'excess_shortage', 'Jobwork': 'jobwork', 'Journal': 'journal',
            'TDS': 'tds', 'Expense Voucher': 'purchase', 'Sales Return': 'credit_note',
            'Transaction': 'other',
          };

          transactions.push({
            id: generateId(),
            date: parsedDate,
            docNo: docStr,
            narration: narrStr,
            debit: Math.round(debit * 100) / 100,
            credit: Math.round(credit * 100) / 100,
            type: type,
            ref: refs.invoiceRef || refs.billRef || docStr,
            invoiceRef: refs.invoiceRef,
            billRef: refs.billRef,
            chequeRef: refs.chequeRef,
            dnCnRef: refs.dnCnRef,
            paymentRef: refs.paymentRef,
            confidence: 90,
            categoryId: typeToCat[type] || 'other'
          });
        }

        resolve({
          transactions,
          lineCount: rawRows.length - headerRowIndex - 1,
          metadata: { company: '', supplier: '', period: '', openingBalance, closingBalance }
        });

      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read Excel file."));
    reader.readAsArrayBuffer(file);
  });
};
